import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { CoverImageVariants } from "@/components/blog/ResponsiveCoverImage";
import { BlogEarlyAccessCard } from "./BlogEarlyAccessCard";
import {
  POPULAR_TOPIC_LABELS,
  POPULAR_TOPIC_SLUGS,
  type PopularTopicSlug,
} from "@/lib/blog/blogFilters";

interface FeaturedPost {
  slug: string;
  title: string;
  coverImageUrl: string | null;
  coverImageVariants?: CoverImageVariants | null;
}

interface Props {
  featured: FeaturedPost | null;
  onTopicSelect?: (slug: string) => void;
  activeTopic?: PopularTopicSlug | null;
}

// Single source of truth for label → slug lives in
// src/lib/blog/blogFilters.ts. Anchors render as real /blog/?topic=... links
// for crawlability; same-page clicks are intercepted by BlogIndexPage via
// onTopicSelect to avoid a full reload.
const POPULAR_TOPICS: { label: string; slug: PopularTopicSlug }[] =
  POPULAR_TOPIC_SLUGS.map((slug) => ({
    slug,
    label: POPULAR_TOPIC_LABELS[slug],
  }));

export function BlogSidebar({ featured, onTopicSelect, activeTopic }: Props) {
  return (
    <aside className="mt-8 lg:mt-0 space-y-6 lg:sticky lg:top-28 lg:self-start">
      {/* Shared early-access CTA card. Same component as /blog/:slug/ sidebar. */}
      <BlogEarlyAccessCard />

      {/*
        Desktop-only Popular Topics. The mobile/tablet equivalent is the
        compact pill rail rendered by BlogSearchAndFilters, so the two
        never appear together.
      */}
      <div className="hidden lg:block rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h2 className="text-caption font-semibold uppercase text-muted-foreground mb-4">
          Popular topics
        </h2>
        <ul className="divide-y divide-border">
          {POPULAR_TOPICS.map((t) => {
            const active = activeTopic === t.slug;
            return (
              <li key={t.slug} className="first:pt-0 last:pb-0">
                <a
                  href={`/blog/?topic=${t.slug}`}
                  data-active={active ? "true" : undefined}
                  onClick={(e) => {
                    if (onTopicSelect) {
                      e.preventDefault();
                      onTopicSelect(t.slug);
                    }
                  }}
                  className={
                    "py-2.5 flex items-center justify-between gap-3 text-body-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm " +
                    (active
                      ? "text-primary font-semibold"
                      : "text-foreground/80 hover:text-primary")
                  }
                >
                  <span>{t.label}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />
                </a>
              </li>
            );
          })}
        </ul>
      </div>

      {featured && (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <Link
            to={`/blog/${featured.slug}/`}
            className="block group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          >
            <p className="text-caption uppercase text-primary font-semibold mb-2">
              Featured guide
            </p>
            <h3 className="text-h4 group-hover:text-primary transition-colors">
              {featured.title}
            </h3>
            <span className="mt-3 inline-flex items-center text-body-sm font-medium text-primary">
              Read article
              <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>
        </div>
      )}
    </aside>
  );
}